import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { initGA4, trackPageView, setUser as ga4SetUser, ga } from './utils/ga4';
import LandingPage from './pages/LandingPage';
import StorePage from './pages/StorePage';
import PawsDashboard from './pages/paws/PawsDashboard';
import PawsPets from './pages/paws/PawsPets';
import PawsBookings from './pages/paws/PawsBookings';
import PawsCredits from './pages/paws/PawsCredits';
import PawsSubscriptions from './pages/paws/PawsSubscriptions';
import PawsBookingStatus from './pages/paws/PawsBookingStatus';
import PawsSupport from './pages/paws/PawsSupport';
import SupportPage from './pages/SupportPage';
import BookingFlow from './pages/BookingFlow';
import DenDashboard from './pages/den/DenDashboard';
import DenBookings from './pages/den/DenBookings';
import DenServices from './pages/den/DenServices';
import DenUsers from './pages/den/DenUsers';
import DenNodes from './pages/den/DenNodes';
import DenAnalytics from './pages/den/DenAnalytics';
import DenCoupons from './pages/den/DenCoupons';
import DenLeads from './pages/den/DenLeads';
import DenGroomerView from './pages/den/DenGroomerView';
import DenFinancials from './pages/den/DenFinancials';
import DenTemplates from './pages/den/DenTemplates';
import DenBlog from './pages/den/DenBlog';
import TncPage from './pages/TncPage';
import PrivacyPage from './pages/PrivacyPage';
import FranchisePage from './pages/FranchisePage';
import BlogListPage from './pages/BlogListPage';
import BlogPostPage from './pages/BlogPostPage';
import PawsReferral from './pages/paws/PawsReferral';
import Header from './components/Header';
import DenLayout from './components/DenLayout';
import PawsLayout from './components/PawsLayout';
import AuthModal from './components/AuthModal';
import OnboardingModal from './components/OnboardingModal';
import Toast from './components/Toast';

const API = process.env.REACT_APP_BACKEND_URL;

// Domain detection
const hostname = window.location.hostname;

// Only apply domain-split routing on actual production domains
const IS_PROD_PAWS = hostname === 'petfolk.in';
const IS_PROD_DEN  = hostname === 'den.petfolk.in';

// Exported constants — used by Header, DenLayout etc.
export const IS_DEN_DOMAIN = IS_PROD_DEN;
export const DEN_URL  = 'https://den.petfolk.in';
export const PAWS_URL = 'https://petfolk.in';

// --- Auth Context ---
export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// --- API Helper ---
async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('petfolk_token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
}

export { apiFetch };

// --- Toast Context ---
export const ToastContext = createContext(null);
export function useToast() { return useContext(ToastContext); }

// --- Theme Context ---
export const ThemeContext = createContext(null);
export function useTheme() { return useContext(ThemeContext); }

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('petfolk_dark') === 'true');

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const toggleDark = useCallback(() => {
    setDarkMode(prev => {
      localStorage.setItem('petfolk_dark', !prev);
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [darkMode]);

  // Initialize GA4 on app mount
  useEffect(() => { initGA4(); }, []);

  // Update GA4 user_id whenever auth state changes
  useEffect(() => {
    if (user) {
      ga4SetUser(user.id, user.role);
    } else {
      ga4SetUser(null);
    }
  }, [user]);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('petfolk_token');
    if (token) {
      apiFetch('/api/auth/me')
        .then(u => { setUser(u); setLoading(false); })
        .catch(() => { localStorage.removeItem('petfolk_token'); setLoading(false); });
    } else {
      setLoading(false);
      // Show onboarding only on petfolk.in (not den.petfolk.in) for first-time visitors
      const skipOnboard = new URLSearchParams(window.location.search).get('skip') === '1';
      const noModalPaths = ['/den', '/paws', '/tnc', '/privacy', '/blog'];
      const onCustomerRoute = !noModalPaths.some(p => window.location.pathname.startsWith(p));
      if (!IS_DEN_DOMAIN && !skipOnboard && !localStorage.getItem('petfolk_onboarded') && onCustomerRoute) {
        setShowOnboarding(true);
      }
    }
  }, []);

  const login = async (identifier, otp, name, email) => {
    const res = await apiFetch('/api/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ identifier, otp, name, email }),
    });
    localStorage.setItem('petfolk_token', res.token);
    setUser(res.user);
    // GA4: track login or sign_up depending on whether the user was just created
    const method = identifier && identifier.includes('@') ? 'email_otp' : 'phone_otp';
    if (res.is_new_user) {
      ga.signUp(method, res.user.role);
    } else {
      ga.login(method, res.user.role);
    }
    return res.user;
  };

  const logout = () => {
    localStorage.removeItem('petfolk_token');
    setUser(null);
    addToast('Signed out', 'info');
  };

  const refreshUser = async () => {
    try {
      const u = await apiFetch('/api/auth/me');
      setUser(u);
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--bg-primary)' }}>
        <div className="flex flex-col items-center gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/r89kksa2_Petfolk%20Paw%20Multi.png"
            alt="Petfolk"
            className="w-16 h-16 object-contain animate-pulse"
          />
          <p className="font-outfit text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Loading Petfolk...</p>
        </div>
      </div>
    );
  }

  const isDen = window.location.pathname.startsWith('/den');

  // ── Den route tree (reused across two branches) ──
  const denRoutes = (
    <Route path="/den" element={<DenLayout />}>
      <Route index element={<DenDashboard />} />
      <Route path="bookings"   element={<DenBookings />} />
      <Route path="services"   element={<DenServices />} />
      <Route path="users"      element={<DenUsers />} />
      <Route path="nodes"      element={<DenNodes />} />
      <Route path="analytics"  element={<DenAnalytics />} />
      <Route path="coupons"    element={<DenCoupons />} />
      <Route path="leads"      element={<DenLeads />} />
      <Route path="groomer"    element={<DenGroomerView />} />
      <Route path="financials" element={<DenFinancials />} />
      <Route path="templates" element={<DenTemplates />} />
      <Route path="blog" element={<DenBlog />} />
    </Route>
  );

  // ── Customer route tree (reused across two branches) ──
  const customerRoutes = (
    <>
      <Route path="/"               element={<LandingPage />} />
      <Route path="/support"        element={<SupportPage />} />
      <Route path="/franchise"      element={<FranchisePage />} />
      <Route path="/tnc"            element={<TncPage />} />
      <Route path="/privacy"        element={<PrivacyPage />} />
      <Route path="/blog"           element={<BlogListPage />} />
      <Route path="/blog/:slug"     element={<BlogPostPage />} />
      <Route path="/book/:nodeSlug" element={<BookingFlow />} />
      <Route path="/paws" element={<PawsLayout />}>
        <Route index element={<PawsDashboard />} />
        <Route path="pets"           element={<PawsPets />} />
        <Route path="bookings"       element={<PawsBookings />} />
        <Route path="bookings/:id"   element={<PawsBookingStatus />} />
        <Route path="credits"        element={<PawsCredits />} />
        <Route path="subscriptions"  element={<PawsSubscriptions />} />
        <Route path="referral"       element={<PawsReferral />} />
        <Route path="support"        element={<PawsSupport />} />
      </Route>
      {/* Store pages — must be last */}
      <Route path="/:slug" element={<StorePage />} />
    </>
  );

  return (
    <HelmetProvider>
    <ThemeContext.Provider value={{ darkMode, toggleDark }}>
      <ToastContext.Provider value={addToast}>
        <AuthContext.Provider value={{ user, login, logout, setShowAuth, refreshUser, showAuth }}>
          <BrowserRouter>

            {/* ── BRANCH 1: den.petfolk.in — Admin portal only ── */}
            {IS_PROD_DEN ? (
              <Routes>
                {denRoutes}
                {/* Any non-den path on den.petfolk.in → redirect to /den */}
                <Route path="*" element={<Navigate to="/den" replace />} />
              </Routes>

            /* ── BRANCH 2: petfolk.in — Customer portal + /den accessible ── */
            ) : IS_PROD_PAWS ? (
              <>
                {!isDen && <Header />}
                <Routes>
                  {customerRoutes}
                  {denRoutes}
                </Routes>
                {showOnboarding && (
                  <OnboardingModal onClose={() => { setShowOnboarding(false); localStorage.setItem('petfolk_onboarded', 'true'); }} />
                )}
              </>

            /* ── BRANCH 3: localhost / preview — all routes, no domain split ── */
            ) : (
              <>
                {!isDen && <Header />}
                <Routes>
                  {customerRoutes}
                  {denRoutes}
                </Routes>
                {showOnboarding && (
                  <OnboardingModal onClose={() => { setShowOnboarding(false); localStorage.setItem('petfolk_onboarded', 'true'); }} />
                )}
              </>
            )}

            {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
            <Toast toasts={toasts} />
            <RouteChangeTracker />
          </BrowserRouter>
        </AuthContext.Provider>
      </ToastContext.Provider>
    </ThemeContext.Provider>
    </HelmetProvider>
  );
}

/** Fires GA4 page_view on every SPA route change. */
function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

/** Redirects /den visitors on petfolk.in to den.petfolk.in */
function DenCrossRedirect() {
  useEffect(() => {
    window.location.replace(DEN_URL);
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
      <p className="font-outfit text-lg" style={{ color: 'var(--text-secondary)' }}>
        Redirecting to den.petfolk.in…
      </p>
    </div>
  );
}

export default App;
